import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const StatRow = ({ label, homeVal, awayVal }) => {
  const h = parseFloat(homeVal) || 0;
  const a = parseFloat(awayVal) || 0;
  const max = Math.max(h, a, 0.001);
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-[rgba(101,141,113,0.2)] last:border-0">
      <div className="flex items-center gap-1.5 flex-1 justify-end">
        <span className={`text-[10px] font-black tabular-nums ${h >= a ? 'text-[#a9c9b1]' : 'text-white'}`}>{homeVal ?? '--'}</span>
        <div className="h-1.5 bg-[rgba(0,0,0,0.3)] rounded-full overflow-hidden w-14">
          <div className="h-full rounded-full" style={{ width: `${(h / max) * 100}%`, backgroundColor: '#a9c9b1' }} />
        </div>
      </div>
      <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold w-14 text-center shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 flex-1">
        <div className="h-1.5 bg-[rgba(0,0,0,0.3)] rounded-full overflow-hidden w-14">
          <div className="h-full rounded-full" style={{ width: `${(a / max) * 100}%`, backgroundColor: '#c4333e' }} />
        </div>
        <span className={`text-[10px] font-black tabular-nums ${a > h ? 'text-[#c4333e]' : 'text-white'}`}>{awayVal ?? '--'}</span>
      </div>
    </div>
  );
};

const AnalyticsHomeAway = ({ allGames }) => {
  const { homeStats, awayStats, radarOption } = useMemo(() => {
    if (!allGames?.length) return { homeStats: null, awayStats: null, radarOption: null };
    const finished = allGames.filter(g => g.status?.abstractGameState === 'Final');
    const home = { w: 0, l: 0, rs: 0, ra: 0, games: 0 };
    const away = { w: 0, l: 0, rs: 0, ra: 0, games: 0 };

    finished.forEach(g => {
      const bosHome = g.teams?.home?.team?.id === 111;
      const bosScore = Number(bosHome ? g.teams.home.score : g.teams.away.score);
      const oppScore = Number(bosHome ? g.teams.away.score : g.teams.home.score);
      const won = bosScore > oppScore;
      const bucket = bosHome ? home : away;
      bucket.games++;
      bucket.rs += bosScore;
      bucket.ra += oppScore;
      if (won) bucket.w++;
      else bucket.l++;
    });

    const rpg = b => b.games ? (b.rs / b.games).toFixed(2) : '0.00';
    const rapg = b => b.games ? (b.ra / b.games).toFixed(2) : '0.00';
    const wpct = b => b.games ? ((b.w / b.games) * 100).toFixed(1) : '0.0';
    const rd = b => (b.rs - b.ra);

    const radar = {
      backgroundColor: 'transparent',
      radar: {
        indicator: [
          { name: 'Win %', max: 100 }, { name: 'R/G', max: 8 }, { name: 'RA/G', max: 8 },
          { name: 'W', max: Math.max(home.w, away.w, 1) }, { name: 'Run Diff', max: Math.max(Math.abs(rd(home)), Math.abs(rd(away)), 1) },
        ],
        shape: 'polygon',
        axisName: { color: '#a9c9b1', fontSize: 9, fontWeight: 'bold' },
        splitLine: { lineStyle: { color: 'rgba(101,141,113,0.3)' } },
        splitArea: { areaStyle: { color: ['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.1)'] } },
        axisLine: { lineStyle: { color: 'rgba(101,141,113,0.4)' } },
      },
      tooltip: { backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
      legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
      series: [{
        type: 'radar',
        data: [
          { value: [parseFloat(wpct(home)), parseFloat(rpg(home)), parseFloat(rapg(home)), home.w, Math.max(rd(home), 0)], name: 'Home', lineStyle: { color: '#a9c9b1', width: 2 }, areaStyle: { color: 'rgba(169,201,177,0.15)' }, itemStyle: { color: '#a9c9b1' } },
          { value: [parseFloat(wpct(away)), parseFloat(rpg(away)), parseFloat(rapg(away)), away.w, Math.max(rd(away), 0)], name: 'Away', lineStyle: { color: '#c4333e', width: 2 }, areaStyle: { color: 'rgba(196,51,62,0.15)' }, itemStyle: { color: '#c4333e' } },
        ],
      }],
    };

    return {
      homeStats: { ...home, rpg: rpg(home), rapg: rapg(home), wpct: wpct(home), rd: rd(home) },
      awayStats: { ...away, rpg: rpg(away), rapg: rapg(away), wpct: wpct(away), rd: rd(away) },
      radarOption: radar,
    };
  }, [allGames]);

  if (!homeStats) return <div className="text-[#658d71] text-xs text-center py-8 uppercase tracking-widest">No data available</div>;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        {[{ label: 'Home', stats: homeStats, color: '#a9c9b1' }, { label: 'Away', stats: awayStats, color: '#c4333e' }].map(({ label, stats, color }) => (
          <div key={label} className="border border-[#658d71] p-3 bg-[rgba(0,0,0,0.08)] flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color }}>{label}</span>
              <span className="text-[9px] text-[#658d71] font-bold uppercase">{stats.games}G</span>
            </div>
            <div className="flex items-end gap-1">
              <span className="text-3xl font-black tabular-nums text-white">{stats.w}</span>
              <span className="text-[#658d71] font-black text-xl mb-0.5">–</span>
              <span className="text-3xl font-black tabular-nums text-[#658d71]">{stats.l}</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {[['Win%', `${stats.wpct}%`], ['R/G', stats.rpg], ['RA/G', stats.rapg], ['Run Diff', stats.rd > 0 ? `+${stats.rd}` : stats.rd]].map(([k, v]) => (
                <div key={k} className="flex flex-col border border-[rgba(101,141,113,0.3)] p-1.5 bg-[rgba(0,0,0,0.1)]">
                  <span className="text-[8px] text-[#658d71] uppercase tracking-widest font-bold">{k}</span>
                  <span className="text-xs font-black tabular-nums text-white">{v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Home vs Away Radar</span>
        </div>
        <div className="p-2"><ReactECharts option={radarOption} style={{ height: '220px' }} /></div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex justify-between">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Split Comparison</span>
          <div className="flex gap-4 text-[9px] font-black uppercase tracking-widest">
            <span className="text-[#a9c9b1]">HOME</span><span className="text-[#c4333e]">AWAY</span>
          </div>
        </div>
        <div className="p-3">
          {[['Win%', `${homeStats.wpct}%`, `${awayStats.wpct}%`], ['R/G', homeStats.rpg, awayStats.rpg], ['RA/G', homeStats.rapg, awayStats.rapg]].map(([l, h, a]) => (
            <StatRow key={l} label={l} homeVal={h} awayVal={a} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsHomeAway;